import type { RenderStateCamera } from "@novorender/api";
import { vec3 } from "gl-matrix";

import { ELEMENT_IDS } from "features/bookmarks/utils";

export const DRAG_THRESHOLD = 5;

export function getSvgElement(): SVGSVGElement | null {
    const svg = document.getElementById(ELEMENT_IDS.markersContainer);
    return svg as SVGSVGElement | null;
}

export function getPickRay(
    camera: RenderStateCamera,
    x: number,
    y: number,
    cssWidth: number,
    cssHeight: number,
    renderWidth: number,
    renderHeight: number,
): { origin: vec3; dir: vec3 } {
    if (camera.kind !== "pinhole" || !("fov" in camera)) {
        throw new Error(
            `getPickRay only supports perspective (pinhole) cameras. Got camera.kind="${camera.kind}". Use view.pick() for orthographic cameras.`,
        );
    }

    if (cssWidth <= 0 || cssHeight <= 0 || renderWidth <= 0 || renderHeight <= 0) {
        throw new Error(
            `Invalid dimensions for getPickRay: cssWidth=${cssWidth}, cssHeight=${cssHeight}, renderWidth=${renderWidth}, renderHeight=${renderHeight}. All dimensions must be positive.`,
        );
    }

    const renderX = (x / cssWidth) * renderWidth;
    const renderY = (y / cssHeight) * renderHeight;

    const aspect = renderWidth / renderHeight;
    const fovRad = (camera.fov * Math.PI) / 180;
    const tanFov2 = Math.tan(fovRad / 2);

    const px = (2 * (renderX + 0.5)) / renderWidth - 1;
    const py = 1 - (2 * (renderY + 0.5)) / renderHeight;

    const dirView = vec3.fromValues(px * aspect * tanFov2, py * tanFov2, -1);
    vec3.normalize(dirView, dirView);

    const dirWorld = vec3.create();
    vec3.transformQuat(dirWorld, dirView, camera.rotation);

    return { origin: camera.position, dir: dirWorld };
}
